import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { installApiErrorInterceptor } from "./utils/apiError";

// RC-WEB-6 PHASE 3 — no API error object can reach React as a child.
installApiErrorInterceptor();

import EnvironmentBadge from "./components/EnvironmentBadge";
import PushReconciler from "./components/PushReconciler";
import PushForegroundListener from "./components/push/PushForegroundListener";

// AVISOCAR DOMAIN INTEGRATION
import { isProductionDomain, getApiBaseUrl } from "./config/domain";

// Log domain status on app load
console.log("=".repeat(50));
console.log("AVISOCAR APP INITIALIZED");
console.log("Domain:", window.location.host);
console.log("Production mode:", isProductionDomain());
console.log("API URL:", getApiBaseUrl());
console.log("=".repeat(50));

if (isProductionDomain()) {
  console.log("%c✅ AVISOCAR DOMAIN ACTIVE", "color: #10b981; font-weight: bold; font-size: 16px;");
}

// Pages
import Landing from "./pages/Landing";
import InstallGuide from "./pages/InstallGuide";
import HowToUse from "./pages/HowToUse";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import NewProtection from "./pages/NewProtection";
import Protections from "./pages/Protections";
import VehicleCreate from "./pages/VehicleCreate";
import VehicleDetail from "./pages/VehicleDetail";
import Checkout from "./pages/Checkout";
import Messages from "./pages/Messages";
import Calls from "./pages/Calls";
import StickerStudio from "./pages/StickerStudio";
import Settings from "./pages/Settings";
import PublicQR from "./pages/PublicQRv2";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsersCenter from "./pages/AdminUsersCenter";
import AdminUserDetail from "./pages/AdminUserDetail";
import AdminAddUser from "./pages/AdminAddUser";
import AdminPayments from "./pages/AdminPayments";
import AdminVehicles from "./pages/AdminVehicles";
import AdminPaymentConfig from "./pages/AdminPaymentConfig";
import AdminTwilioStatus from "./pages/AdminTwilioStatus";
import AdminLogs from "./pages/AdminLogs";
import AdminRuntimeLogs from "./pages/AdminRuntimeLogs";
import OwnerRespond from "./pages/OwnerRespond";
import AdminSiteContent from "./pages/AdminSiteContent";
// iter64 — legacy /admin/memberships removed (consolidated into /admin/subscriptions from iter62-63)
import AdminSubscriptions from "./pages/AdminSubscriptions"; // iter62
import AdminPricingDisplay from "./pages/AdminPricingDisplay"; // iter64 corte 4
import AdminMembershipPlans from "./pages/AdminMembershipPlans"; // RC-WEB-2
import AdminCoupons from "./pages/AdminCoupons"; // CORTE 5-A
import RenewMembership from "./pages/Checkout"; // RC-WEB-6 — /renovar uses the canonical checkout
import PartnerView from "./pages/PartnerView"; // CORTE 5-B
import AdminPartners from "./pages/AdminPartners"; // CORTE 5-C
import LegalPage from "./pages/LegalPage";
import DeleteAccount from "./pages/DeleteAccount";
import TrustedContacts from "./pages/TrustedContacts";
import TrustedContactInvitation from "./pages/TrustedContactInvitation";
import AdminStickerTemplates from "./pages/AdminStickerTemplates";
import AdminSharedContent from "./pages/AdminSharedContent";
import AdminSafetyCenter from "./pages/AdminSafetyCenter";
import AdminLegalDocuments from "./pages/AdminLegalDocuments";
import AdminPushHealth from "./pages/AdminPushHealth";
import AdminShell from "./components/admin/AdminShell";
import DebugQrFlow from "./pages/DebugQrFlow";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./i18n";
import { PricingProvider } from "./context/PricingContext";
import { InboxProvider } from "./context/InboxContext";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Only Route (redirect if logged in)
const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

// RC-WEB-6 PHASE 5 — historical payment links keep working, preserving the
// protected-profile context, but the old `Realizar Pago` UI is unreachable.
const LegacyPaymentRedirect = () => {
  const { vehicleId } = useParams();
  return <Navigate to={vehicleId ? `/checkout/${vehicleId}` : "/renovar"} replace />;
};

function AppRoutes() {
  // CORTE 5-B — atribución ?ref=CODIGO: localStorage (visita→registro) + visita al embudo
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref && ref.trim() && !window.__avisocarRefSent) {
        window.__avisocarRefSent = true; // guard StrictMode: un solo POST por carga
        const code = ref.trim().toUpperCase().slice(0, 32);
        localStorage.setItem("avisocar_ref", code);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ref/visit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        }).catch(() => {});
        // Limpia ?ref= de la URL sin recargar
        const url = new URL(window.location.href);
        url.searchParams.delete("ref");
        window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      }
    } catch (_) { /* noop */ }
  }, []);
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/partner/:token" element={<PartnerView />} />  {/* CORTE 5-B: vista pública del partner */}
      {/* RC-WEB-3 §8 — destino público duradero; la guía PWA queda como ayuda opcional */}
      <Route path="/como-usar" element={<HowToUse />} />
      <Route path="/how-to-use" element={<Navigate to="/como-usar" replace />} />
      <Route path="/ayuda/usar-en-navegador" element={<InstallGuide />} />
      <Route path="/como-instalar" element={<Navigate to="/ayuda/usar-en-navegador" replace />} />
      <Route path="/nosotros" element={<About />} />
      <Route path="/contacto" element={<Contact />} />
      <Route path="/v/:token" element={<PublicQR />} />
      <Route path="/q/:token" element={<PublicQR />} />  {/* New Edge Engine route */}
      <Route path="/respond" element={<OwnerRespond />} />  {/* Magic-link owner response (no login) */}
      <Route path="/terminos" element={<LegalPage />} />
      <Route path="/privacidad" element={<LegalPage />} />
      <Route path="/sobre-nosotros" element={<LegalPage />} />
      <Route path="/contacto" element={<LegalPage />} />
      <Route path="/reclamaciones" element={<LegalPage />} />
      {/* U1 F7 — recurso web público de eliminación de cuenta (Apple + Google Play) */}
      <Route path="/eliminar-cuenta" element={<DeleteAccount />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/contacto-de-confianza" element={<TrustedContactInvitation />} />
      <Route path="/trusted-contact-invitation" element={<TrustedContactInvitation />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={
        <PublicOnlyRoute><Login /></PublicOnlyRoute>
      } />
      <Route path="/register" element={
        <PublicOnlyRoute><Register /></PublicOnlyRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>
      } />
      <Route path="/reset-password" element={
        <PublicOnlyRoute><ResetPassword /></PublicOnlyRoute>
      } />
      
      {/* User Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/new-protection" element={
        <ProtectedRoute><NewProtection /></ProtectedRoute>
      } />
      <Route path="/dashboard/vehicles" element={
        <ProtectedRoute><Protections /></ProtectedRoute>
      } />
      <Route path="/dashboard/vehicles/new" element={
        <ProtectedRoute><VehicleCreate /></ProtectedRoute>
      } />
      <Route path="/dashboard/vehicles/:id" element={
        <ProtectedRoute><VehicleDetail /></ProtectedRoute>
      } />
      {/* RC-WEB-6 PHASE 5 — ONE canonical checkout. Every legacy payment URL
          redirects here KEEPING the protected-profile context. */}
      <Route path="/checkout/:protectionId" element={
        <ProtectedRoute><Checkout /></ProtectedRoute>
      } />
      <Route path="/checkout" element={<Navigate to="/renovar" replace />} />
      <Route path="/dashboard/payment/:vehicleId" element={<LegacyPaymentRedirect />} />
      <Route path="/payment/pending/:vehicleId" element={<LegacyPaymentRedirect />} />
      <Route path="/dashboard/messages" element={<Navigate to="/mensajes" replace />} />
      <Route path="/dashboard/profile" element={
        <ProtectedRoute><Settings /></ProtectedRoute>
      } />
      <Route path="/dashboard/contactos-de-confianza" element={
        <ProtectedRoute><TrustedContacts /></ProtectedRoute>
      } />
      <Route path="/contactos-de-confianza" element={
        <ProtectedRoute><TrustedContacts /></ProtectedRoute>
      } />
      <Route path="/trusted-contacts" element={<Navigate to="/contactos-de-confianza" replace />} />
      
      {/* RC-WEB-4 §3 — ONE canonical inbox. Every legacy inbox URL redirects here. */}
      <Route path="/mensajes" element={
        <ProtectedRoute><Messages /></ProtectedRoute>
      } />
      <Route path="/messages" element={<Navigate to="/mensajes" replace />} />
      <Route path="/inbox" element={<Navigate to="/mensajes" replace />} />
      <Route path="/dashboard/inbox" element={<Navigate to="/mensajes" replace />} />
      <Route path="/llamadas" element={
        <ProtectedRoute><Calls /></ProtectedRoute>
      } />
      <Route path="/calls" element={<Navigate to="/llamadas" replace />} />
      <Route path="/sticker-studio" element={
        <ProtectedRoute><StickerStudio /></ProtectedRoute>
      } />
      <Route path="/debug/qr-flow/:token" element={
        <ProtectedRoute><DebugQrFlow /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute><Settings /></ProtectedRoute>
      } />
      <Route path="/vehicle/:id" element={
        <ProtectedRoute><VehicleDetail /></ProtectedRoute>
      } />
      <Route path="/pay/:vehicleId" element={<LegacyPaymentRedirect />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute requireAdmin><AdminUsersCenter /></ProtectedRoute>
      } />
      <Route path="/admin/users/add" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminAddUser /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/users/:userId" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminUserDetail /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute requireAdmin><AdminPayments /></ProtectedRoute>
      } />
      <Route path="/admin/vehicles" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminVehicles /></AdminShell></ProtectedRoute>
      } />
      {/* iter64 — /admin/memberships route removed; redirect legacy paths to /admin/subscriptions */}
      <Route path="/admin/memberships" element={<Navigate to="/admin/subscriptions" replace />} />
      <Route path="/admin/subscriptions" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminSubscriptions /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/membership-plans" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminMembershipPlans /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/pricing-display" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminPricingDisplay /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/coupons" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminCoupons /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/partners" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminPartners /></AdminShell></ProtectedRoute>
      } />
      <Route path="/renovar" element={
        <ProtectedRoute><RenewMembership /></ProtectedRoute>
      } />
      <Route path="/admin/config" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminPaymentConfig /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/twilio" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminTwilioStatus /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/logs" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminLogs /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/runtime-logs" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminRuntimeLogs /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/contenido" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminSiteContent /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/site-content" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminSiteContent /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/sticker-templates" element={
        <ProtectedRoute requireAdmin><AdminStickerTemplates /></ProtectedRoute>
      } />
      <Route path="/admin/shared-content" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminSharedContent /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/safety" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminSafetyCenter /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/legal" element={
        <ProtectedRoute requireAdmin><AdminShell flush><AdminLegalDocuments /></AdminShell></ProtectedRoute>
      } />
      <Route path="/admin/push-health" element={
        <ProtectedRoute requireAdmin><AdminPushHealth /></ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
    <PricingProvider>
    <AuthProvider>
      <InboxProvider>
      <BrowserRouter>
        <AppRoutes />
        <EnvironmentBadge />
        <PushReconciler />
        <PushForegroundListener />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </BrowserRouter>
      </InboxProvider>
    </AuthProvider>
    </PricingProvider>
    </I18nProvider>
  );
}

export default App;
